import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Checkbox, TextInput } from '@mantine/core';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import PaginationList from '../../common/PaginationList';
import SelectableListItem from '../../common/SelectableListItem';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notificationHelper';

const SyncDivisionTeamsModal = ({
  isOpen,
  onClose,
  pkRegAssociationSeasons,
  pkRegAssociationDivisions,
  regAssociationDivisionTeams
}) => {
  const { state: dashboardState } = useContext(
    RegistrationAdminDashboardContext
  );
  const {
    state,
    fetchRegAssociationDivisionTeams,
    fetchRegAssociationDivisionTeamOptions,
    syncRegAssociationSeasonTeams
  } = useContext(RegistrationAdminContext);
  const [formState, setFormState] = useState({
    loading: false,
    pkRegAssociationDivisionTeams: [],
    search: '',
    pageIndex: 1
  });

  const teams = (
    state.regAssociationDivisionTeamOptions.value
      ?.regAssociationDivisionTeams ||
    regAssociationDivisionTeams ||
    []
  ).sort(
    (a, b) =>
      a.name.localeCompare(b.name) || a.seasonName.localeCompare(b.seasonName)
  );

  const filteredTeams = teams.filter(
    (f) =>
      !formState.search ||
      f.name.toLowerCase().includes(formState.search.toLowerCase())
  );

  const allTeamsSelected = filteredTeams.every((t) =>
    formState.pkRegAssociationDivisionTeams.includes(
      t.pkRegAssociationDivisionTeam
    )
  );

  useEffect(() => {
    if (isOpen) {
      setFormState({
        loading: false,
        pkRegAssociationDivisionTeams: regAssociationDivisionTeams
          ? regAssociationDivisionTeams.map(
              (t) => t.pkRegAssociationDivisionTeam
            )
          : [],
        search: '',
        pageIndex: 1
      });

      if (pkRegAssociationSeasons || pkRegAssociationDivisions) {
        fetchRegAssociationDivisionTeamOptions(
          dashboardState.regAssociation.value.pkRegAssociation,
          {
            pkRegAssociationSeasons,
            pkRegAssociationDivisions
          },
          (data) => {
            setFormState({
              ...formState,
              pkRegAssociationDivisionTeams: data.regAssociationDivisionTeams.map(
                (t) => t.pkRegAssociationDivisionTeam
              )
            });
          }
        );
      }
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      centered
      formSectionProps={{
        isLoading: formState.loading,
        onCancel: onClose,
        onSubmit: () => {
          setFormState({
            ...formState,
            loading: true
          });

          syncRegAssociationSeasonTeams(
            dashboardState.regAssociation.value.pkRegAssociation,
            {
              pkRegAssociationDivisionTeams:
                formState.pkRegAssociationDivisionTeams
            },
            () => {
              if (regAssociationDivisionTeams) {
                fetchRegAssociationDivisionTeams({
                  pkRegAssociation:
                    dashboardState.regAssociation.value.pkRegAssociation
                });
              }

              onClose();
              triggerNotification(
                `${teams.length === 1 ? 'Team' : 'Teams'} launched`,
                'Success',
                'green'
              );
            },
            (e) => {
              triggerNotification(e);
              setFormState({
                ...formState,
                loading: false
              });
            }
          );
        },
        submitTitle: 'Launch',
        isSubmitDisabled: formState.pkRegAssociationDivisionTeams.length === 0
      }}
      onClose={onClose}
      opened={isOpen}
      title={
        regAssociationDivisionTeams && regAssociationDivisionTeams.length === 1
          ? `Launch ${teams[0].name}`
          : 'Launch Teams'
      }
    >
      <AppStack style={{ gap: 10, marginTop: 20 }}>
        {!state.regAssociationDivisionTeamOptions.loading &&
        teams.length === 0 ? (
          <AppStack style={{ gap: 8 }}>
            <AppText style={{ fontSize: 14, textAlign: 'center' }}>
              No teams found for this{' '}
              {pkRegAssociationDivisions
                ? 'division'
                : pkRegAssociationSeasons
                ? 'season'
                : ''}
              .
            </AppText>
          </AppStack>
        ) : (
          <AppStack style={{ gap: 8 }}>
            {regAssociationDivisionTeams &&
            regAssociationDivisionTeams.length === 1 ? (
              <AppStack style={{ gap: 8 }}>
                <AppText style={{ fontSize: 16, textAlign: 'center' }}>
                  Are you sure you want to launch{' '}
                  <b>{regAssociationDivisionTeams[0].name}</b>?
                </AppText>
              </AppStack>
            ) : (
              <>
                <AppFlexbox
                  style={{
                    gap: 5,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                    Teams
                  </AppText>

                  {!state.regAssociationDivisionTeamOptions.loading && (
                    <Checkbox
                      checked={allTeamsSelected}
                      disabled={formState.loading}
                      label="Select all"
                      onChange={() => {
                        setFormState({
                          ...formState,
                          pkRegAssociationDivisionTeams: [
                            ...formState.pkRegAssociationDivisionTeams.filter(
                              (t) =>
                                !filteredTeams
                                  .map((f) => f.pkRegAssociationDivisionTeam)
                                  .includes(t)
                            ),
                            ...(allTeamsSelected
                              ? []
                              : filteredTeams.map(
                                  (t) => t.pkRegAssociationDivisionTeam
                                ))
                          ]
                        });
                      }}
                    />
                  )}
                </AppFlexbox>
                <TextInput
                  disabled={formState.loading}
                  onChange={(e) => {
                    setFormState({
                      ...formState,
                      search: e.currentTarget.value,
                      pageIndex: 1
                    });
                  }}
                  placeholder="Search..."
                  value={formState.search}
                />
                <PaginationList
                  emptyMessage="No teams found"
                  isLoading={state.regAssociationDivisionTeamOptions.loading}
                  items={filteredTeams.map((p) => {
                    const selected = formState.pkRegAssociationDivisionTeams.includes(
                      p.pkRegAssociationDivisionTeam
                    );

                    return (
                      <AppFlexbox
                        key={p.pkRegAssociationDivisionTeam}
                        onClick={() => {
                          setFormState({
                            ...formState,
                            pkRegAssociationDivisionTeams: selected
                              ? formState.pkRegAssociationDivisionTeams.filter(
                                  (id) => id !== p.pkRegAssociationDivisionTeam
                                )
                              : [
                                  ...formState.pkRegAssociationDivisionTeams,
                                  p.pkRegAssociationDivisionTeam
                                ]
                          });
                        }}
                        style={{
                          flex: 1,
                          padding: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer'
                        }}
                      >
                        <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                          <Checkbox
                            checked={selected}
                            disabled={formState.loading}
                            onChange={() => {}}
                          />
                          <AppStack style={{ gap: 0 }}>
                            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                              {p.name}
                            </AppText>
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              {p.divisionName} ({p.seasonName})
                            </AppText>
                          </AppStack>
                        </AppFlexbox>
                      </AppFlexbox>
                    );
                  })}
                  itemsPerPage={10}
                  LoadingComponent={SelectableListItem}
                  onPageChange={(pageIndex) =>
                    setFormState({ ...formState, pageIndex })
                  }
                  pageIndex={formState.pageIndex}
                />
              </>
            )}
          </AppStack>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

SyncDivisionTeamsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  pkRegAssociationDivisions: PropTypes.array,
  pkRegAssociationSeasons: PropTypes.array,
  regAssociationDivisionTeams: PropTypes.array
};

export default SyncDivisionTeamsModal;
